import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Type } from "lucide-react";

export default function FontsPage() {
  const [fonts, setFonts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fontName, setFontName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get("/api/v1/fonts").then(setFonts).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fontName) { toast.error("Enter a font name first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", fontName);
      const result = await api.upload("/api/v1/fonts", fd);
      setFonts([{ id: result.id, name: fontName, file_url: result.url, format: file.name.split(".").pop() }, ...fonts]);
      setFontName("");
      toast.success("Font uploaded!");
    } catch (e: any) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this font?")) return;
    try { await api.delete(`/api/v1/fonts/${id}`); setFonts(fonts.filter(f => f.id !== id)); toast.success("Deleted"); }
    catch (e: any) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Fonts</h1>
      </div>
      <div className="bg-card rounded-xl border border-border p-4 mb-6 flex items-center gap-3">
        <input value={fontName} onChange={(e) => setFontName(e.target.value)} placeholder="Font name" className="w-48 px-3 py-2 rounded-lg border border-input bg-background text-sm" />
        <label className={`px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2 cursor-pointer ${uploading ? "opacity-50" : ""}`}>
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload Font"}
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept=".ttf,.otf,.woff,.woff2" />
        </label>
      </div>
      {loading ? <div className="animate-pulse space-y-3">{[1,2].map(i=><div key={i} className="h-16 bg-muted rounded-lg"/>)}</div> : fonts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No fonts uploaded yet.</div>
      ) : (
        <div className="space-y-2">
          {fonts.map(f => (
            <div key={f.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Type className="w-5 h-5 text-primary" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{f.name}</p>
                <p className="text-xs text-muted-foreground">{f.format?.toUpperCase()}</p>
              </div>
              <button onClick={() => handleDelete(f.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
